import { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import { User, Mail, Phone, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function ArtistProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editableFields, setEditableFields] = useState({
    email: "",
    phone: ""
  });

  const fetchProfile = useCallback(async () => {
    try {
      const res = await apiClient.get("/artist/my-profile");
      setProfile(res.data);
      setEditableFields({
        email: res.data.email || "",
        phone: res.data.phone || ""
      });
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.patch("/artist/my-profile", editableFields);
      toast.success("Profile updated");
      fetchProfile();
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="artist-profile">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>
          My Profile
        </h1>
      </div>

      <div className="max-w-2xl">
        {/* Profile Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-light">{profile?.display_name || profile?.name}</h2>
                <p className="text-muted-foreground">{profile?.specialty || "Artist"}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Editable Fields */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Contact Information</CardTitle>
            <CardDescription>Update your contact details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input 
                type="email"
                value={editableFields.email}
                onChange={(e) => setEditableFields({ ...editableFields, email: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Phone
              </Label>
              <Input 
                type="tel"
                value={editableFields.phone}
                onChange={(e) => setEditableFields({ ...editableFields, phone: e.target.value })}
              />
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full mt-4">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </CardContent>
        </Card>

        {/* Read-only Fields */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Account Information</CardTitle>
            <CardDescription>These fields cannot be edited</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground text-xs">Name</Label>
                <p className="font-medium">{profile?.display_name || profile?.name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Role</Label>
                <p className="font-medium">Artist</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Specialty</Label>
                <p className="font-medium">{profile?.specialty || "—"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Status</Label>
                <p className="font-medium">{profile?.status || "Active"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
